import axios from "axios";

export const getCurrentFrontendRoute = () => {
  if (typeof window === 'undefined' || !window.location) return null;
  const { pathname, search, hash } = window.location;
  return `${pathname || '/'}${search || ''}${hash || ''}`;
};

export const applyCurrentFrontendRouteHeader = (headers = {}) => {
  const route = getCurrentFrontendRoute();
  if (!route) return headers;

  if (typeof headers.set === 'function') {
    headers.set('x-frontend-route', route);
    return headers;
  }

  headers['x-frontend-route'] = route;
  return headers;
};

export const getFrontendRouteHeader = () => {
  const route = getCurrentFrontendRoute();
  return route ? { 'x-frontend-route': route } : {};
};



export const labsSortingFunction = (contextLabs, userLabs) => {
  const indexMap = new Map();
  contextLabs.forEach((item) => {
    indexMap.set(item.item_number, item.index);
  });
  userLabs.sort((a, b) => {
    const indexA = indexMap.get(a.item_number) ?? Infinity;
    const indexB = indexMap.get(b.item_number) ?? Infinity;
    return indexA - indexB;
  });
  return userLabs;
};

export const dispensesSortingFunction = (contextDispenses, userdispensed) => {
  const indexMap = new Map();
  contextDispenses.forEach((item) => {
    indexMap.set(item.name, item.index);
  });
  userdispensed.sort((a, b) => {
    const indexA = indexMap.get(a.name) ?? Infinity;
    const indexB = indexMap.get(b.name) ?? Infinity;
    return indexA - indexB;
  });
  return userdispensed;
};

export const proceduresSortingFunction = (
  contextProcedures,
  userProcedures
) => {
  const indexMap = new Map();
  contextProcedures.forEach((item) => {
    indexMap.set(item.procedure_name, item.index);
  });
  userProcedures.sort((a, b) => {
    const indexA = indexMap.get(a.procedure_name) ?? Infinity;
    const indexB = indexMap.get(b.procedure_name) ?? Infinity;
    return indexA - indexB;
  });
  return userProcedures;
};

export const getLabDisplayName = (contextLabs, lab) => {
  if (!Array.isArray(contextLabs) || contextLabs.length === 0) {
    return lab.test_name;
  }

  const foundLab = contextLabs.find(
    (item) => item.item_number === lab.item_number
  );
  if (foundLab) {
    return foundLab.display_name;
  }
  return null;
};

export const getProcedureDisplayName = (contextProcedures, procedureName) => {
  if (!Array.isArray(contextProcedures) || contextProcedures.length === 0) {
    return procedureName;
  }

  const foundProcedure = contextProcedures.find(
    (item) => item.procedure_name === procedureName
  );
  if (foundProcedure && foundProcedure.display_name) {
    return foundProcedure.display_name;
  }
  return procedureName;
};

export const getDispensedDisplayName = (contextDispensed, fullName) => {
  if (!Array.isArray(contextDispensed) || contextDispensed.length === 0) {
    return fullName;
  }

  let baseName = fullName;
  baseName = baseName.replace(/ \([^)]*\) x\d+$/, '');
  baseName = baseName.replace(/ \([^)]*\)$/, '');

  const item = contextDispensed.find((disp) => disp.name === baseName);

  if (item && item.display_name && baseName !== fullName) {
    const suffix = fullName.substring(baseName.length);
    return item.display_name + suffix;
  }

  return (item && item.display_name) ? item.display_name : fullName;
};

export const getSupplementDisplayName = (contextSupplements, supplementName) => {
  if (!Array.isArray(contextSupplements) || contextSupplements.length === 0) {
    return supplementName;
  }

  const foundSupplement = contextSupplements.find(
    (item) => item.supplement_name === supplementName
  );
  if (foundSupplement && foundSupplement.display_name) {
    return foundSupplement.display_name;
  }
  return supplementName;
};


// API Utility Functions - DRY Principle for LPDS Data Fetching
export const fetchAllLabs = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/allLabs`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("sessionToken")}`,
      },
    });
    return response.data.allLabs;
  } catch (error) {
    console.error("Error fetching labs:", error);
    throw error;
  }
};

export const fetchAllProcedures = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/allProcedures`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("sessionToken")}`,
      },
    });
    return response.data.allProcedures;
  } catch (error) {
    console.error("Error fetching procedures:", error);
    throw error;
  }
};

export const fetchAllDispensed = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/allDispensed`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("sessionToken")}`,
      },
    });
    return response.data.allDispensed;
  } catch (error) {
    console.error("Error fetching dispensed items:", error);
    throw error;
  }
};

export const fetchAllSupplements = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/getAllSupplements`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("sessionToken")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching supplements:", error);
    throw error;
  }
};

// Combined function to fetch all LPDS data at once
export const fetchAllLPDSData = async () => {
  try {
    const [labs, procedures, dispensed, supplements] = await Promise.all([
      fetchAllLabs(),
      fetchAllProcedures(),
      fetchAllDispensed(),
      fetchAllSupplements(),
    ]);
    return {
      labs,
      procedures,
      dispensed,
      supplements,
    };
  } catch (error) {
    console.error("Error fetching LPDS data:", error);
    throw error;
  }
};

// Fetch all leads with messages (paginated)
export const getAllLeads = async (offset = 0, limit = 100) => {
  try {
    const sessionToken = localStorage.getItem('sessionToken');
    if (!sessionToken) {
      throw new Error('No session token found');
    }

    const response = await axios.get(`${BASE_URL}/leads/messages`, {
      params: { offset, limit },
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
    });

    if (response.status === 200) {
      return response.data;
    }
    return { leads: [], hasMore: false, total: 0 };
  } catch (error) {
    console.error("Error fetching leads:", error);
    throw error;
  }
};

export const hasG6PDLab = (treatment) => {
  if (!treatment || !treatment.userLabs) {
    return false;
  }
  const G6PDLab = treatment.userLabs.find(lab => lab.test_name === "G6PD");
  return G6PDLab?.checked === true;
};

export const g6pdLabResult = (treatment) => {
  if (!treatment || !treatment.userLabs) {
    return false;
  }
  const G6PDLab = treatment.userLabs.find(lab => lab.test_name === "G6PD");
  return Boolean(G6PDLab?.result);
};

export const g6pdLabInRange = (treatment) => {
  if (!treatment || !treatment.userLabs) {
    return false;
  }
  const G6PDLab = treatment.userLabs.find(lab => lab.test_name === "G6PD");
  if (!G6PDLab || G6PDLab.range === undefined || G6PDLab.range === null) {
    return false;
  }
  const range = Number(G6PDLab.range);
  // G6PD is safe/cleared when the value is in range OR above it (per Dr. Franco).
  // Only a value BELOW the threshold (deficiency) is a concern, so there is no
  // upper bound — e.g. 246 is in-range/safe, not a concern. (Meeting task 53.)
  return !isNaN(range) && range > 100;
};

export const getPatientFormStatus = (formDetails) => {
  if (!formDetails) {
    return { text: "Unknown Status", style: "bg-gray-50 text-gray-600 border-gray-200" };
  }
  
  // If both submitted and approved, show only "Approved"
  if (formDetails.isSubmitted && formDetails.isApproved) {
    return { text: "Approved", style: "bg-green-50 text-green-700 border-green-200" };
  }
  
  // If submitted but not approved, show "Waiting for Approval"
  if (formDetails.isSubmitted && !formDetails.isApproved) {
    return { text: "Waiting for Approval", style: "bg-yellow-50 text-yellow-700 border-yellow-200" };
  }
  
  // If link generated but not submitted, show "Waiting for Submission"
  if (formDetails.linkGenerated && !formDetails.isSubmitted) {
    return { text: "Waiting for Submission", style: "bg-blue-50 text-blue-700 border-blue-200" };
  }
  
  return { text: "Unknown Status", style: "bg-gray-50 text-gray-600 border-gray-200" };
};

export const getTreatmentConsultationStatus = (formDetails) => {
  if (!formDetails) {
    return { text: "Unknown Status", style: "bg-gray-50 text-gray-600 border-gray-200" };
  }
  
  // If consultation is submitted, show "Submitted"
  if (formDetails.consultIsSubmitted) {
    return { text: "Submitted", style: "bg-green-50 text-green-700 border-green-200" };
  }
  
  // If consultation link is generated but not submitted, show "Generated"
  if (formDetails.isConsultLinkGenerated && !formDetails.consultIsSubmitted) {
    return { text: "Generated", style: "bg-blue-50 text-blue-700 border-blue-200" };
  }
  
  return { text: "Unknown Status", style: "bg-gray-50 text-gray-600 border-gray-200" };
};

export const calculateAge = (dob) => {
  if (!dob) {
    return null;
  }
  
  const birthDate = new Date(dob);
  
  // Check for invalid date
  if (isNaN(birthDate.getTime())) {
    return null;
  }
  
  const today = new Date();
  
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--; // Adjust age if birthday hasn't occurred yet this year
  }

  return age;
};

export function splitByLastDash(columnName) {
  if (!columnName || typeof columnName !== 'string') {
    console.error("Invalid input: columnName must be a non-empty string");
    return columnName;
  }
  
  // Find the index of the last dash
  const lastIndex = columnName.lastIndexOf("-");

  // If there's no dash, or the dash is the first character, return the original
  if (lastIndex <= 0) {
    console.warn(
      "No dash found, or the dash is at the start of the string"
    );
    return columnName;
  }

  // Split the string into two parts
  const itemName = columnName.substring(0, lastIndex);
  const field = columnName.substring(lastIndex + 1);

  return [itemName, field];
}

export function getBackgroundColor(type) {
  if (type) {
    if (type === "Treatment") {
      return "bg-blue-500 hover:!bg-blue-400";
    } else if (type === "Order" || type === "pickup") {
      return "bg-orange-500 hover:!bg-orange-400";
    }
  }
  return "";
}

export const formatArizonaPhoneNumber = (input) => {
  try {
    if (!input || typeof input !== 'string') {
      return null;
    }
    
    const digits = input.replace(/\D/g, '');

    let cleaned = '';

    if (digits.length === 10) {
      cleaned = `1${digits}`; 
    } else if (digits.length === 11 && digits.startsWith('1')) {
      cleaned = digits;
    } else {
      // Return null instead of throwing
      console.error('Invalid phone number format');
      return null;
    }

    const areaCode = cleaned.slice(1, 4);
    const centralOfficeCode = cleaned.slice(4, 7);
    const lineNumber = cleaned.slice(7);

    return `+1 (${areaCode}) ${centralOfficeCode}-${lineNumber}`;
  } catch (error) {
    console.error('Error formatting phone number:', error);
    return null;
  }
};


export const replaceEmailTemplatePlaceholders = (template, replacements) => {
  return Object.entries(replacements).reduce((result, [key, value]) => {
    const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
    return result.replace(regex, value);
  }, template);
}

export const getOptimalAddress = (addresses) => {
  if (!addresses || !Array.isArray(addresses) || addresses.length === 0) {
    return null;
  }

  if (addresses.length === 1) {
    return addresses[0];
  }

  const addressWithScores = addresses.map((address, index) => {
    const fieldCount = Object.values(address || {}).filter(value =>
      value !== null &&
      value !== undefined &&
      value !== '' &&
      (typeof value !== 'string' || value.trim() !== '')
    ).length;

    return {
      address,
      fieldCount,
      originalIndex: index
    };
  });

  const maxFieldCount = Math.max(...addressWithScores.map(item => item.fieldCount));
  const addressesWithMaxFields = addressWithScores.filter(item => item.fieldCount === maxFieldCount);
  const selectedAddress = addressesWithMaxFields.find(item => item.originalIndex === 0) || addressesWithMaxFields[0];

  return selectedAddress.address;
};

export const BASE_URL = process.env.REACT_APP_API_URL || 'https://vwwjda2e99.us-west-2.awsapprunner.com';
